import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
// Removida importação do lucide-react
import { Empenho, EmpenhoFormatado } from "../../../types/empenhos";
import { apiService } from "../../../services/api";
import ModalEmpenho from "./ModalEmpenho";

interface EmpenhosListProps {
    contratoId: number;
    formatCurrency: (value: number) => string;
    formatDate: (dateString: string) => string;
    onUpdateComplete: () => void;
}

const EmpenhosList: React.FC<EmpenhosListProps> = ({
    contratoId,
    formatCurrency,
    formatDate,
    onUpdateComplete
}) => {
    const [empenhos, setEmpenhos] = useState<EmpenhoFormatado[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmpenho, setSelectedEmpenho] = useState<EmpenhoFormatado | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [dropdownAberto, setDropdownAberto] = useState<{[key: string]: boolean}>({});
    const [produtosDropdownAberto, setProdutosDropdownAberto] = useState<{[key: number]: boolean}>({});
    const [produtosContrato, setProdutosContrato] = useState<any[]>([]);
    const [empenhosDetalhados, setEmpenhosDetalhados] = useState<{[key: number]: any[]}>({});

    // Carregar produtos do contrato para comparação
    const loadProdutosContrato = async () => {
        try {
            const contratoRes = await apiService.getContratoById(contratoId);
            if (contratoRes.success && contratoRes.data && contratoRes.data.id_oportunidade) {
                const produtosRes = await apiService.getProdutosOportunidade(contratoRes.data.id_oportunidade);
                if (produtosRes.success && produtosRes.data) {
                    setProdutosContrato(produtosRes.data);
                }
            }
        } catch (error) {
            console.error("Erro ao carregar produtos do contrato:", error);
        }
    };

    // Carregar empenhos do contrato
    const loadEmpenhos = async () => {
        try {
            setLoading(true);
            const empenhosRes = await apiService.getEmpenhos({ id_contrato: contratoId });

            if (empenhosRes.success && empenhosRes.data) {
                const empenhosFormatados = empenhosRes.data.map((empenho: Empenho) => ({
                    ...empenho,
                    numero_empenho: empenho.codigo_empenho || `EMP-${empenho.id}`,
                    status: empenho.situacao
                }));
                setEmpenhos(empenhosFormatados);

                // Carregar detalhes de cada empenho (itens)
                const detalhesPromises = empenhosFormatados.map(async (empenho) => {
                    try {
                        const detalhesRes = await apiService.getEmpenhoById(empenho.id);
                        if (detalhesRes.success && detalhesRes.data) {
                            return { id: empenho.id, itens: detalhesRes.data.itens || [] };
                        }
                    } catch (error) {
                        console.error(`Erro ao carregar detalhes do empenho ${empenho.id}:`, error);
                    }
                    return { id: empenho.id, itens: [] };
                });

                const detalhesResultados = await Promise.all(detalhesPromises);
                const empenhosDetalhesMap = detalhesResultados.reduce((acc, resultado) => {
                    acc[resultado.id] = resultado.itens;
                    return acc;
                }, {} as {[key: number]: any[]});
                
                setEmpenhosDetalhados(empenhosDetalhesMap);
            }

            // Carregar produtos do contrato
            await loadProdutosContrato();
        } catch (error) {
            console.error("Erro ao carregar empenhos:", error);
            toast.error("Erro ao carregar empenhos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmpenhos();
    }, [contratoId]);

    // Funções para manipular empenhos
    const handleEdit = (empenho: EmpenhoFormatado) => {
        setSelectedEmpenho(empenho);
        setShowEditModal(true);
    };

    const handleDelete = (empenho: EmpenhoFormatado) => {
        setSelectedEmpenho(empenho);
        setShowDeleteModal(true);
    };

    const handleAddEmpenho = () => {
        setSelectedEmpenho(null);
        setShowAddModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedEmpenho) return;

        try {
            await apiService.deleteEmpenho(selectedEmpenho.id);
            toast.success("Empenho excluído com sucesso!");
            loadEmpenhos();
            onUpdateComplete();
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Erro ao excluir empenho:", error);
            toast.error("Erro ao excluir empenho");
        }
    };

    const handleEmpenhoSaved = () => {
        loadEmpenhos();
        onUpdateComplete();
        setShowEditModal(false);
        setShowAddModal(false);
    };

    // Agrupar empenhos por número
    const empenhosAgrupados = empenhos.reduce((grupos: { [key: string]: EmpenhoFormatado[] }, empenho) => {
        const numero = empenho.codigo_empenho || empenho.numero_empenho || `EMP-${empenho.id}`;
        if (!grupos[numero]) {
            grupos[numero] = [];
        }
        grupos[numero].push(empenho);
        return grupos;
    }, {});

    // Calcular totais e porcentagens
    const calcularResumo = (listaEmpenhos: EmpenhoFormatado[]) => {
        const valorTotal = listaEmpenhos.reduce((total, emp) => total + Number(emp.valor_total || 0), 0);
        const valorPago = listaEmpenhos
            .filter(emp => emp.situacao === "Entregue")
            .reduce((total, emp) => total + Number(emp.valor_total || 0), 0);
        const percentualPago = valorTotal > 0 ? (valorPago / valorTotal) * 100 : 0;

        return {
            valorTotal,
            valorPago,
            percentualPago: percentualPago.toFixed(0)
        };
    };

    // Calcular porcentagem de produto empenhado em relação ao total do contrato
    const calcularPorcentagemProduto = (idProduto: number, quantidadeEmpenhada: number) => {
        const produtoContrato = produtosContrato.find(p => p.id_produto === idProduto);
        if (!produtoContrato || !produtoContrato.quantidade) return 0;
        
        const porcentagem = (quantidadeEmpenhada / produtoContrato.quantidade) * 100;
        return Math.min(porcentagem, 100); // Limita a 100%
    };

    // Obter nome do produto
    const getNomeProduto = (idProduto: number) => {
        const produtoContrato = produtosContrato.find(p => p.id_produto === idProduto);
        return produtoContrato?.nome_produto || 'Produto não encontrado';
    };

    // Alternar estado do dropdown
    const toggleDropdown = (numero: string) => {
        setDropdownAberto(prev => {
            const novoEstado = {
                ...prev,
                [numero]: !prev[numero]
            };
            
            // Se estamos abrindo o dropdown do empenho, também abrir os produtos automaticamente
            if (novoEstado[numero]) {
                // Encontrar todos os empenhos deste grupo e abrir seus produtos
                const empenhosDoGrupo = empenhosAgrupados[numero] || [];
                const novosDropdownsProdutos = { ...produtosDropdownAberto };
                
                empenhosDoGrupo.forEach(empenho => {
                    if (empenhosDetalhados[empenho.id] && empenhosDetalhados[empenho.id].length > 0) {
                        novosDropdownsProdutos[empenho.id] = true;
                    }
                });
                
                setProdutosDropdownAberto(novosDropdownsProdutos);
            }
            
            return novoEstado;
        });
    };

    // Alternar estado do dropdown de produtos
    const toggleProdutosDropdown = (empenhoId: number) => {
        setProdutosDropdownAberto(prev => ({
            ...prev,
            [empenhoId]: !prev[empenhoId]
        }));
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-semibold">Empenhos</h4>
                    <div>
                        <button
                            className="btn btn-primary"
                            onClick={handleAddEmpenho}
                        >
                            <i className="ti ti-plus me-1"></i>
                            Adicionar Empenho
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : Object.keys(empenhosAgrupados).length === 0 ? (
                        <div className="alert alert-info">
                            Nenhum empenho registrado para este contrato.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>N° Empenho</th>
                                        <th>Valor Total</th>
                                        <th>Valor Pago</th>
                                        <th>% Pago</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(empenhosAgrupados).map(([numero, listaEmpenhos]) => {
                                        const { valorTotal, valorPago, percentualPago } = calcularResumo(listaEmpenhos);
                                        const isAberto = dropdownAberto[numero] || false;
                                        
                                        return (
                                            <React.Fragment key={numero}>
                                                <tr className="bg-light">
                                                    <td>
                                                        <div 
                                                            className="d-flex align-items-center cursor-pointer" 
                                                            onClick={() => toggleDropdown(numero)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {isAberto ? (
                                                                <i className="ti ti-chevron-up me-2"></i>
                                                            ) : (
                                                                <i className="ti ti-chevron-down me-2"></i>
                                                            )}
                                                            <span className="fw-medium">{numero}</span>
                                                        </div>
                                                    </td>
                                                    <td className="fw-medium">{formatCurrency(valorTotal)}</td>
                                                    <td className="fw-medium">{formatCurrency(valorPago)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                                                <div
                                                                    className="progress-bar bg-success"
                                                                    role="progressbar"
                                                                    style={{ width: `${percentualPago}%` }}
                                                                    aria-valuenow={Number(percentualPago)}
                                                                    aria-valuemin={0}
                                                                    aria-valuemax={100}
                                                                ></div>
                                                            </div>
                                                            <span className="ms-2">{percentualPago}%</span>
                                                        </div>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                                {isAberto && listaEmpenhos.map((empenho, idx) => (
                                                    <React.Fragment key={`${numero}-${idx}`}>
                                                        <tr>
                                                            <td className="ps-4">
                                                                <div className="d-flex justify-content-between">
                                                                    <span>{empenho.numero_empenho}</span>
                                                                    <span className="text-muted">{formatDate(empenho.data_empenho)}</span>
                                                                </div>
                                                            </td>
                                                            <td>{formatCurrency(empenho.valor_total)}</td>
                                                            <td>
                                                                {empenho.situacao === "Entregue"
                                                                    ? formatCurrency(empenho.valor_total)
                                                                    : formatCurrency(0)}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${
                                                                    empenho.situacao === 'Entregue' ? 'bg-success' :
                                                                    empenho.situacao === 'Em trânsito' ? 'bg-info' :
                                                                    'bg-warning'
                                                                }`}>
                                                                    {empenho.situacao || 'Pendente'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    {empenhosDetalhados[empenho.id] && empenhosDetalhados[empenho.id].length > 0 && (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => toggleProdutosDropdown(empenho.id)}
                                                                            title="Ver produtos"
                                                                        >
                                                                            {produtosDropdownAberto[empenho.id] ? (
                                                                                <i className="ti ti-chevron-up"></i>
                                                                            ) : (
                                                                                <i className="ti ti-chevron-down"></i>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                    <Link
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        to="#"
                                                                        onClick={() => handleEdit(empenho)}
                                                                    >
                                                                        <i className="ti ti-edit"></i>
                                                                    </Link>
                                                                    <Link
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        to="#"
                                                                        onClick={() => handleDelete(empenho)}
                                                                    >
                                                                        <i className="ti ti-trash"></i>
                                                                    </Link>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {/* Produtos do empenho integrados */}
                                                        {produtosDropdownAberto[empenho.id] && empenhosDetalhados[empenho.id] && empenhosDetalhados[empenho.id].length > 0 && empenhosDetalhados[empenho.id].map((item: any, itemIdx: number) => {
                                                            const porcentagem = calcularPorcentagemProduto(item.id_produto, item.quantidade);
                                                            const nomeProduto = getNomeProduto(item.id_produto);
                                                            
                                                            return (
                                                                <tr key={`produto-${empenho.id}-${itemIdx}`} style={{ backgroundColor: '#e9ecef' }}>
                                                                    <td className="ps-5 py-1">
                                                                        <div className="d-flex align-items-center">
                                                                            <i className="ti ti-corner-down-right text-dark me-2" style={{ fontSize: '12px' }}></i>
                                                                            <span className="text-dark small">
                                                                                {nomeProduto}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-1">
                                                                        <span className="small text-dark">
                                                                            {formatCurrency(item.valor_unitario * item.quantidade)}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-1">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <span className="text-dark small">
                                                                                {Number(item.quantidade).toString()} un.
                                                                            </span>
                                                                            <span className={`badge ${
                                                                                porcentagem >= 100 ? 'bg-success' :
                                                                                porcentagem >= 50 ? 'bg-warning' :
                                                                                'bg-info'
                                                                            } small`}>
                                                                                {porcentagem.toFixed(1)}%
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-1"></td>
                                                                    <td className="py-1"></td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                                {isAberto && (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-muted">
                                                            {listaEmpenhos.length} empenho(s) encontrado(s)
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para adicionar empenho */}
            <ModalEmpenho
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                contratoId={contratoId}
                empenho={null}
                onSave={handleEmpenhoSaved}
            />

            {/* Modal para editar empenho */}
            {selectedEmpenho && (
                <ModalEmpenho
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    contratoId={contratoId}
                    empenho={selectedEmpenho}
                    onSave={handleEmpenhoSaved}
                />
            )}

            {/* Confirm Delete Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <div className="modal-body">
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Excluir Empenho?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja excluir o empenho <br />
                            <strong>{selectedEmpenho?.codigo_empenho || selectedEmpenho?.numero_empenho}</strong>?
                        </p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EmpenhosList;